import { useEffect, useState, type RefObject } from 'react';

/**
 * Whether a sticky element has left its resting place in its parent. Measured rather than
 * CSS `scroll-state(stuck)`, which Firefox and Safari lack.
 */
export function useStuck(ref: RefObject<HTMLElement | null>): boolean {
    const [stuck, setStuck] = useState(false);

    useEffect(() => {
        const element = ref.current;
        const parent = element?.parentElement;
        if (!element || !parent) {
            return;
        }

        let frame = 0;
        const update = (): void => {
            frame = 0;
            const resting =
                Number.parseFloat(getComputedStyle(parent).paddingTop) +
                Number.parseFloat(getComputedStyle(element).marginTop);
            const offset = element.getBoundingClientRect().top - parent.getBoundingClientRect().top;
            setStuck(offset > resting + 0.5);
        };
        const schedule = (): void => {
            if (!frame) {
                frame = requestAnimationFrame(update);
            }
        };

        update();
        window.addEventListener('scroll', schedule, { capture: true, passive: true });
        window.addEventListener('resize', schedule, { passive: true });
        return () => {
            cancelAnimationFrame(frame);
            window.removeEventListener('scroll', schedule, { capture: true });
            window.removeEventListener('resize', schedule);
        };
    }, [ref]);

    return stuck;
}
